import { supabase } from '../lib/supabase';

export interface CompressedImageResult {
  blob: Blob;
  base64: string;
  originalSizeKb: number;
  compressedSizeKb: number;
  compressionRatioPercent: number;
  width: number;
  height: number;
}

/**
 * Comprime uma foto no navegador usando a Canvas API.
 * Redimensiona a imagem para no máximo `maxDimension` x `maxDimension` mantendo aspect-ratio
 * e converte para WebP (com fallback para JPEG), gerando arquivos de apenas 15KB a 35KB.
 */
export async function compressProductImage(
  file: File | Blob,
  maxDimension = 500,
  quality = 0.8
): Promise<CompressedImageResult> {
  return new Promise((resolve, reject) => {
    const originalSizeKb = Math.round((file.size / 1024) * 10) / 10;
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Falha ao ler o arquivo de imagem'));
    reader.onload = e => {
      const img = new Image();
      img.onerror = () => reject(new Error('Falha ao carregar a imagem selecionada'));
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Mantém a proporção redimensionando para caber dentro do quadrado maxDimension
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Falha ao inicializar contexto Canvas'));
          return;
        }

        // Suavização de imagem de alta qualidade
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Desenha a imagem redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Tenta exportar como WebP; se o navegador não suportar, usa JPEG
        let mimeType = 'image/webp';
        let base64 = canvas.toDataURL(mimeType, quality);
        if (!base64.startsWith('data:image/webp')) {
          mimeType = 'image/jpeg';
          base64 = canvas.toDataURL(mimeType, quality);
        }

        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Falha ao gerar blob comprimido'));
              return;
            }

            const compressedSizeKb = Math.round((blob.size / 1024) * 10) / 10;
            const savedBytes = Math.max(0, originalSizeKb - compressedSizeKb);
            const compressionRatioPercent = originalSizeKb > 0 
              ? Math.round((savedBytes / originalSizeKb) * 100) 
              : 0;

            resolve({
              blob,
              base64,
              originalSizeKb,
              compressedSizeKb,
              compressionRatioPercent,
              width,
              height
            });
          },
          mimeType,
          quality
        );
      };

      img.src = e.target?.result as string;
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Envia a imagem comprimida para o bucket 'product-photos' do Supabase Storage.
 * Retorna a URL pública pronta para salvar no banco de dados.
 */
export async function uploadProductPhoto(
  userId: string,
  blob: Blob,
  productId?: string
): Promise<string> {
  const fileExt = blob.type.includes('webp') ? 'webp' : 'jpg';
  const cleanProdId = productId ? productId.replace(/[^a-zA-Z0-9_-]/g, '') : 'new';
  const fileName = `${userId}/${cleanProdId}_${Date.now()}.${fileExt}`;

  const { data, error } = await supabase.storage
    .from('product-photos')
    .upload(fileName, blob, {
      contentType: blob.type,
      upsert: true,
      cacheControl: '31536000' // Cache de 1 ano
    });

  if (error) {
    throw error;
  }

  const { data: publicData } = supabase.storage
    .from('product-photos')
    .getPublicUrl(data.path);

  return publicData.publicUrl;
}
